import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { sanitizeHTML } from '@/lib/sanitize';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Clock, ChevronLeft, ChevronRight, Flag, Send, Home,
  CheckCircle, Circle, AlertTriangle, BookOpen, UserCheck,
  Calendar, Users, ArrowRight, Loader2, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatTime } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Memoized component to prevent re-rendering of question content (fixes flickering)
const QuestionContent = memo(function QuestionContent({ html }) {
  const sanitizedHtml = useMemo(() => sanitizeHTML(html), [html]);
  return (
    <div 
      className="text-lg text-slate-800 leading-relaxed question-content"
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
});

// Memoized option content
const OptionContent = memo(function OptionContent({ html }) {
  const sanitizedHtml = useMemo(() => sanitizeHTML(html), [html]);
  return <span className="option-text" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
});

// ============== VERIFICATION PAGE ==============
function VerificationPage({ onVerified }) {
  const [participantNumber, setParticipantNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeEvents, setActiveEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    loadActiveEvents();
  }, []);

  const loadActiveEvents = async () => {
    try {
      const res = await fetch(`${API}/public/event-tryouts/active`);
      const data = await res.json();
      setActiveEvents(data);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!participantNumber.trim()) {
      toast.error('Masukkan nomor peserta');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/public/event-tryouts/verify-participant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant_number: participantNumber.trim() })
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.detail || 'Nomor peserta tidak valid');
        return;
      }

      if (!data.valid) {
        toast.error(data.message || 'Tidak dapat mengakses event');
        return;
      }

      // Verified successfully
      onVerified(data);
    } catch (err) {
      console.error('Verification error:', err);
      toast.error('Gagal memverifikasi. Coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0047AB] rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">MyTentor</h1>
                <p className="text-xs text-slate-500">Event Try Out</p>
              </div>
            </div>
            <a href="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <Home className="w-4 h-4" />
              <span className="text-sm">Beranda</span>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Verification Form */}
        <Card className="shadow-xl border-0 mb-8" data-testid="event-verification-card">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-[#0047AB]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-[#0047AB]" />
            </div>
            <CardTitle className="text-2xl">Event Try Out</CardTitle>
            <CardDescription className="text-base">
              Masukkan nomor peserta untuk memulai ujian
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleVerify} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="participant_number" className="text-base">Nomor Peserta</Label>
                <Input
                  id="participant_number"
                  placeholder="Contoh: EVT-ABC123-0001"
                  value={participantNumber}
                  onChange={(e) => setParticipantNumber(e.target.value.toUpperCase())}
                  className="text-center text-lg font-mono h-14 tracking-wider"
                  data-testid="participant-number-input"
                />
                <p className="text-sm text-slate-500 text-center">
                  Nomor peserta diberikan oleh penyelenggara event
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || !participantNumber.trim()}
                className="w-full h-12 text-base bg-[#0047AB] hover:bg-[#0047AB]/90"
                data-testid="verify-participant-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Memverifikasi...
                  </>
                ) : (
                  <>
                    Verifikasi & Lanjutkan
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Active Events List */}
        {activeEvents.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0047AB]" />
              Event Sedang Berlangsung
            </h2>
            <div className="grid gap-4">
              {activeEvents.map((event) => (
                <Card key={event.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{event.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">{event.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-3 h-3" />
                            {event.tryout_title}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {event.participant_count} peserta
                          </span>
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        Aktif
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {loadingEvents && (
          <div className="text-center py-8 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Memuat event...
          </div>
        )}
      </main>
    </div>
  );
}

// ============== CONFIRMATION PAGE ==============
function ConfirmationPage({ verificationData, onStart, onBack }) {
  const [loading, setLoading] = useState(false);
  const { participant, event, tryout } = verificationData;

  const handleStart = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/event-tryouts/start-exam`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          participant_number: participant.participant_number,
          event_id: event.id
        })
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.detail || 'Gagal memulai ujian');
        return;
      }

      onStart(data);
    } catch (err) {
      console.error('Start exam error:', err);
      toast.error('Gagal memulai ujian. Coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0047AB] rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">MyTentor</h1>
                <p className="text-xs text-slate-500">Event Try Out</p>
              </div>
            </div>
            <Button variant="outline" onClick={onBack} className="gap-2">
              <ChevronLeft className="w-4 h-4" />
              Kembali
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <Card className="shadow-xl border-0" data-testid="event-confirmation-card">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Verifikasi Berhasil!</CardTitle>
            <CardDescription className="text-base">
              Pastikan data berikut sudah benar sebelum memulai ujian
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Participant Info */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-3">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                Data Peserta
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Nama:</span>
                  <p className="font-medium text-slate-900">{participant.name}</p>
                </div>
                <div>
                  <span className="text-slate-500">No. Peserta:</span>
                  <p className="font-mono font-medium text-slate-900">{participant.participant_number}</p>
                </div>
                {participant.school && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Asal Sekolah/Instansi:</span>
                    <p className="font-medium text-slate-900">{participant.school}</p>
                  </div>
                )}
                <div>
                  <span className="text-slate-500">Percobaan ke:</span>
                  <p className="font-medium text-slate-900">
                    {participant.attempt_count + 1} dari {participant.max_attempts}
                  </p>
                </div>
              </div>
            </div>

            {/* Event Info */}
            <div className="bg-blue-50 rounded-xl p-4 space-y-3">
              <h3 className="font-semibold text-blue-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Informasi Event
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-blue-600">Event:</span>
                  <p className="font-medium text-slate-900">{event.title}</p>
                </div>
                <div>
                  <span className="text-blue-600">Try Out:</span>
                  <p className="font-medium text-slate-900">{tryout.title}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-blue-600">Durasi:</span>
                    <p className="font-medium text-slate-900">{tryout.duration_minutes} menit</p>
                  </div>
                  <div>
                    <span className="text-blue-600">Jumlah Soal:</span>
                    <p className="font-medium text-slate-900">{tryout.question_count} soal</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Perhatian!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Setelah memulai, waktu ujian tidak dapat dihentikan</li>
                  <li>Pastikan koneksi internet stabil</li>
                  <li>Jawaban akan otomatis dikirim saat waktu habis</li>
                </ul>
              </div>
            </div>

            {/* Start Button */}
            <Button
              onClick={handleStart}
              disabled={loading}
              className="w-full h-14 text-lg bg-[#0047AB] hover:bg-[#0047AB]/90"
              data-testid="start-event-exam-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Mempersiapkan Soal...
                </>
              ) : (
                <>
                  Mulai Ujian Sekarang
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ============== EXAM PAGE ==============
function EventExamPage({ examData, onSubmit }) {
  const { session_id, participant, tryout, questions, remaining_seconds } = examData;
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(Math.min(tryout.duration_minutes * 60, remaining_seconds));
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const startTimeRef = useRef(Date.now());

  // Timer
  useEffect(() => {
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Warning at 1 minute
  useEffect(() => {
    if (timeLeft === 60) {
      setShowTimeWarning(true);
      setTimeout(() => setShowTimeWarning(false), 5000);
    }
  }, [timeLeft]);

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;

  const handleSelectAnswer = (questionId, optionId) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionId
    }));
  };

  const handleToggleFlag = (questionId) => {
    setFlagged((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    const answersArray = Object.entries(answers).map(([questionId, optionId]) => ({
      question_id: questionId,
      selected_option_id: optionId
    }));

    try {
      const formData = new FormData();
      formData.append('session_id', session_id);
      formData.append('answers', JSON.stringify(answersArray));

      const res = await fetch(`${API}/public/event-tryouts/submit-exam`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.detail || 'Gagal mengirim jawaban');
        setSubmitting(false);
        return;
      }

      onSubmit(data);
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Gagal mengirim jawaban. Coba lagi.');
      setSubmitting(false);
    }
  };

  const getTimeColor = () => {
    if (timeLeft <= 60) return 'text-red-600 bg-red-100 animate-pulse border-2 border-red-400';
    if (timeLeft <= 300) return 'text-orange-600 bg-orange-50 border border-orange-300';
    return 'text-blue-600 bg-blue-50 border border-blue-200';
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="event-exam-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#0047AB] rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-slate-900">{tryout.title}</h1>
                <p className="text-sm text-slate-500">
                  Peserta: {participant.name} ({participant.participant_number})
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              {/* Timer */}
              <div className={cn("px-5 py-3 rounded-xl font-mono text-2xl font-bold shadow-md", getTimeColor())} data-testid="event-exam-timer">
                <Clock className="w-6 h-6 inline mr-2" />
                <span className="text-3xl">{formatTime(timeLeft)}</span>
              </div>
              
              {/* Progress */}
              <div className="hidden md:block w-48">
                <div className="text-sm text-slate-500 mb-1">
                  {answeredCount}/{questions.length} Terjawab
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              
              {/* Submit Button */}
              <Button 
                onClick={() => setShowSubmitDialog(true)}
                className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/90 font-bold"
                data-testid="event-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Selesai
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Question Panel */}
          <div className="lg:col-span-3">
            <Card className="shadow-lg">
              <CardContent className="p-8">
                {/* Question Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-base px-4 py-1">
                      Soal {currentIndex + 1} dari {questions.length}
                    </Badge>
                    {currentQuestion?.mapel && (
                      <Badge variant="outline" className="text-sm px-3 py-1">
                        {currentQuestion.mapel}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant={flagged.has(currentQuestion?.id) ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleToggleFlag(currentQuestion?.id)}
                    className={flagged.has(currentQuestion?.id) ? "bg-orange-500 hover:bg-orange-600" : ""}
                    data-testid="event-flag-btn"
                  >
                    <Flag className="w-4 h-4 mr-1" />
                    {flagged.has(currentQuestion?.id) ? 'Ditandai' : 'Tandai'}
                  </Button>
                </div>

                {/* Question Text */}
                <div className="mb-8">
                  <QuestionContent html={currentQuestion?.text} />
                </div>

                {/* Options */}
                <div className="space-y-3">
                  {currentQuestion?.options.map((option, i) => (
                    <button
                      key={option.id}
                      onClick={() => handleSelectAnswer(currentQuestion.id, option.id)}
                      className={cn(
                        "w-full text-left p-4 rounded-xl border-2 transition-all",
                        answers[currentQuestion?.id] === option.id
                          ? "border-[#0047AB] bg-[#0047AB]/5"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      )}
                      data-testid={`event-option-${i}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm shrink-0",
                          answers[currentQuestion?.id] === option.id
                            ? "bg-[#0047AB] text-white"
                            : "bg-slate-100 text-slate-600"
                        )}>
                          {String.fromCharCode(65 + i)}
                        </div>
                        <OptionContent html={option.text} />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                    disabled={currentIndex === 0}
                    data-testid="event-prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Sebelumnya
                  </Button>
                  <Button
                    onClick={() => setCurrentIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                    disabled={currentIndex === questions.length - 1}
                    className="bg-[#0047AB] hover:bg-[#0047AB]/90"
                    data-testid="event-next-btn"
                  >
                    Selanjutnya
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Navigation Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-28">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-4">Navigasi Soal</h3>
                <div className="grid grid-cols-5 gap-2">
                  {questions.map((q, i) => (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(i)}
                      className={cn(
                        "w-10 h-10 rounded-lg text-sm font-medium transition-all relative",
                        currentIndex === i
                          ? "bg-[#0047AB] text-white"
                          : answers[q.id]
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      )}
                      data-testid={`event-nav-${i}`}
                    >
                      {i + 1}
                      {flagged.has(q.id) && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
                
                {/* Legend */}
                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-100 rounded" />
                    <span className="text-slate-600">Terjawab</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-slate-100 rounded" />
                    <span className="text-slate-600">Belum dijawab</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-slate-100 rounded relative">
                      <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-orange-500 rounded-full" />
                    </div>
                    <span className="text-slate-600">Ditandai</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Selesai</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <span>Apakah Anda yakin ingin menyelesaikan ujian?</span>
                <div className="bg-slate-50 p-4 rounded-xl space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Terjawab:</span>
                    <span className="font-semibold text-green-600">{answeredCount} soal</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Belum dijawab:</span>
                    <span className="font-semibold text-red-600">{questions.length - answeredCount} soal</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Ditandai:</span>
                    <span className="font-semibold text-orange-600">{flagged.size} soal</span>
                  </div>
                </div>
                {questions.length - answeredCount > 0 && (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="text-sm">Masih ada soal yang belum dijawab!</span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="event-cancel-submit-btn">Kembali</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#0047AB] hover:bg-[#0047AB]/90"
              data-testid="event-confirm-submit-btn"
            >
              {submitting ? 'Mengirim...' : 'Selesai & Kirim'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Time Warning Alert */}
      <AlertDialog open={showTimeWarning} onOpenChange={setShowTimeWarning}>
        <AlertDialogContent className="border-red-500 border-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2 text-xl">
              <AlertTriangle className="w-6 h-6 animate-bounce" />
              PERINGATAN WAKTU!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 p-4 rounded-xl text-center">
                  <p className="text-red-700 font-bold text-lg">
                    Waktu ujian tersisa <span className="text-2xl text-red-600">1 MENIT!</span>
                  </p>
                </div>
                <div className="text-center">
                  <span className="text-slate-600">Soal terjawab: </span>
                  <span className="font-bold text-green-600">{answeredCount}</span>
                  <span className="text-slate-600"> / {questions.length}</span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setShowTimeWarning(false)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              Mengerti, Lanjutkan!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============== RESULT PAGE ==============
function EventResultPage({ resultData, participantInfo }) {
  const navigate = useNavigate();
  
  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (percentage) => {
    if (percentage >= 80) return 'bg-green-50 border-green-200';
    if (percentage >= 60) return 'bg-blue-50 border-blue-200';
    if (percentage >= 40) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0047AB] rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">MyTentor</h1>
                <p className="text-xs text-slate-500">Hasil Ujian</p>
              </div>
            </div>
            <a href="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <Home className="w-4 h-4" />
              <span className="text-sm">Beranda</span>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <Card className="shadow-xl border-0" data-testid="event-result-card">
          <CardHeader className="text-center pb-4">
            <div className="w-20 h-20 bg-[#0047AB]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Trophy className={cn("w-10 h-10", getScoreColor(resultData.percentage))} />
            </div>
            <CardTitle className="text-2xl">Ujian Selesai!</CardTitle>
            <CardDescription className="text-base">
              {participantInfo?.name} ({participantInfo?.participant_number})
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Score Display */}
            <div className={cn("rounded-2xl p-6 border-2 text-center", getScoreBg(resultData.percentage))}>
              <p className="text-sm text-slate-600 mb-2">Nilai Anda</p>
              <p className={cn("text-5xl font-bold", getScoreColor(resultData.percentage))}>
                {resultData.percentage.toFixed(1)}%
              </p>
              <p className="text-lg text-slate-600 mt-2">
                {resultData.score} / {resultData.total_points} poin
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-600">{resultData.correct_count}</p>
                <p className="text-sm text-slate-600">Benar</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-600">{resultData.wrong_count}</p>
                <p className="text-sm text-slate-600">Salah</p>
              </div>
              <div className="bg-slate-100 rounded-xl p-4 text-center">
                <Circle className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-600">{resultData.blank_count}</p>
                <p className="text-sm text-slate-600">Kosong</p>
              </div>
            </div>

            {/* Time Spent */}
            <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-slate-500" />
                <span className="text-slate-600">Waktu Pengerjaan</span>
              </div>
              <span className="font-semibold text-slate-900">
                {Math.floor(resultData.time_spent_seconds / 60)} menit {resultData.time_spent_seconds % 60} detik
              </span>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => window.location.href = '/event-tryout'}
                variant="outline"
                className="w-full"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Kembali ke Event Try Out
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-[#0047AB] hover:bg-[#0047AB]/90"
              >
                <Home className="w-4 h-4 mr-2" />
                Ke Beranda
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ============== MAIN COMPONENT ==============
export default function EventTryoutPage() {
  const [stage, setStage] = useState('verify'); // verify, confirm, exam, result
  const [verificationData, setVerificationData] = useState(null);
  const [examData, setExamData] = useState(null);
  const [resultData, setResultData] = useState(null);

  const handleVerified = (data) => {
    setVerificationData(data);
    setStage('confirm');
  };

  const handleStartExam = (data) => {
    setExamData(data);
    setStage('exam');
  };

  const handleSubmitExam = (result) => {
    setResultData(result);
    setStage('result');
  };

  const handleBack = () => {
    setStage('verify');
    setVerificationData(null);
  };

  switch (stage) {
    case 'verify':
      return <VerificationPage onVerified={handleVerified} />;
    case 'confirm':
      return (
        <ConfirmationPage 
          verificationData={verificationData} 
          onStart={handleStartExam}
          onBack={handleBack}
        />
      );
    case 'exam':
      return <EventExamPage examData={examData} onSubmit={handleSubmitExam} />;
    case 'result':
      return (
        <EventResultPage 
          resultData={resultData} 
          participantInfo={examData?.participant}
        />
      );
    default:
      return <VerificationPage onVerified={handleVerified} />;
  }
}
